/**
 * Faster Estimates — the pure half of the grouped line editor (approved mockup
 * "Faster Estimates", Sleeker v2, Corbin 2026-10-15). Grouping, the device
 * header's pieces, what may split, what warns at send, and how a device and a
 * quantity read. No React here, so every rule has a test.
 */
import { isAssemblyNote } from "@/lib/assembly-note";
import { moveLine, refuseCrossingOption } from "../lineOrder";
import type { FeeAudienceInfo } from "./fee-audience-pick";

export interface EditorLine {
    id: string;
    description: string;
    description_html?: string | null;
    qty: string;
    unit_price: string;
    line_total: string;
    asset_id?: string | null;
    asset_name?: string | null;
    asset_serial?: string | null;
    asset_size?: string | null;
    pricebook_kind?: string | null;
    option_id?: string | null;
    labor_hours?: string | null;
    labor_techs?: number | null;
    labor_rate?: string | null;
    taxable?: boolean | null;
    taxed?: boolean;
    tax_reason?: string | null;
    /** 'hand' = typed / edited by a person; never rewords on its own. */
    wording_source?: string | null;
    /** Plain words before the app reworded the line (the Undo pill). */
    reworded_from?: string | null;
    whole_job?: boolean | null;
    /** Server's read of the line: labor | kit | test | fee | assembly | other. */
    wording_kind?: string | null;
    /** The words "Reword?" would write for this line's device. */
    reword_offer?: string | null;
    /** A replacement's check-first pipe line (pipe-choice-strip.tsx). */
    estimate_id?: string | null;
    pipe_choice_state?: string | null;
    pipe_choice_material?: string | null;
    pipe_choice_hint?: string | null;
    pipe_choice_why?: string | null;
    /** A NEW assembly line: what the customer reads, and the make/model the office keeps (office only). */
    office_assembly?: { customerSees: string; officeName: string | null } | null;
    /** Who put the line here (migration 716): 'gwc' | 'person'; null = an older line. */
    added_by?: string | null;
    /** Plain words for why GWC added it. */
    reason?: string | null;
    /** A person added or changed it: Tidy up, Re-price and Rebuild leave it alone. */
    user_touched?: boolean | null;
    /** Home or business city fee (fee-audience-pick.tsx, migration 717). */
    fee_audience?: FeeAudienceInfo | null;
}

export interface EditorDevice {
    id: string;
    name?: string | null;
    size_label?: string | null;
    serial_number?: string | null;
    manufacturer?: string | null;
    model?: string | null;
    assembly_type?: string | null;
    location_description?: string | null;
    asset_kind?: string | null;
}

export type Piece = "labor" | "kit" | "test" | "fee";
export const PIECES: Array<{ piece: Piece; label: string }> = [
    { piece: "labor", label: "Labor" },
    { piece: "kit", label: "Kit" },
    { piece: "test", label: "Test after repair" },
    { piece: "fee", label: "City fee" },
];

/** "2" not "2.0000"; "1.5" not "1.5000". Never rounds a real fraction away. */
export function formatQty(q: string | number | null | undefined): string {
    const n = Number(q);
    if (!Number.isFinite(n)) return String(q ?? "");
    return String(Math.round(n * 10000) / 10000);
}

const TYPE_CHIP: Record<string, string> = {
    rpz: "RPZ",
    rp: "RPZ",
    rpba: "RPZ",
    dc: "DC",
    dcva: "DCVA",
    dcda: "DCDA",
    rpda: "RPDA",
    pvb: "PVB",
    svb: "SVB",
};

export function deviceTypeChip(d: Pick<EditorDevice, "assembly_type">): string | null {
    const k = String(d.assembly_type ?? "")
        .toLowerCase()
        .replace(/[^a-z]/g, "");
    return TYPE_CHIP[k] ?? (k ? k.toUpperCase() : null);
}

/** '1-1/4"' — the size badge. */
export function sizeBadge(size: string | null | undefined): string | null {
    const s = String(size ?? "").trim();
    if (!s) return null;
    return /["”]$/.test(s) ? s.replace(/”$/, '"') : `${s}"`;
}

const stripSize = (name: string, size: string | null | undefined): string => {
    const s = String(size ?? "")
        .trim()
        .replace(/["”]$/, "");
    if (!s) return name;
    const esc = s.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&");
    return name.replace(new RegExp(`^\\s*${esc}\\s*["”]?\\s*`), "").trim() || name;
};

/** 'Watts LF007' — make + model, else the device's name without its size. */
export function deviceTitle(d: EditorDevice): string {
    const make = String(d.manufacturer ?? "").trim();
    let model = String(d.model ?? "").trim();
    if (make && model.toLowerCase().startsWith(make.toLowerCase())) model = model.slice(make.length).trim();
    const pretty = make && make === make.toUpperCase() ? make[0] + make.slice(1).toLowerCase() : make;
    const head = [pretty, model].filter(Boolean).join(" ");
    if (head) return head;
    const name = stripSize(String(d.name ?? "").trim(), d.size_label);
    return name.split(/\s+[—–-]\s+/)[0] || deviceTypeChip(d) || "Device";
}

/** Mirror of the server's usableLocation (estimateLineWording.ts). */
export function locationOnFile(raw: string | null | undefined): string | null {
    const s = String(raw ?? "")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/[.;,:\s]+$/, "");
    if (s.length < 3 || !/[a-z]/i.test(s) || s.length > 80) return null;
    if (
        /\binformation\b|\bcontact\b|^(unknown|unk|n\/?a|na|none|null|undefined|tbd|see notes?|other|location|-+|\?+)$/i.test(
            s,
        )
    ) {
        return null;
    }
    return s;
}

/** A device the line names but the site list doesn't carry (another site, retired). */
function deviceFromLine(l: EditorLine): EditorDevice {
    return {
        id: l.asset_id!,
        name: l.asset_name ?? null,
        size_label: l.asset_size ?? null,
        serial_number: l.asset_serial,
    };
}

export interface LineSection {
    key: string;
    kind: "device" | "whole" | "none";
    device: EditorDevice | null;
    lines: EditorLine[];
    subtotal: number;
    has: Record<Piece, boolean>;
}

/** What a line contributes to the device header's tiles. */
export function pieceOf(l: EditorLine): Piece | null {
    const k = (l.wording_kind ?? "").toLowerCase();
    if (k === "labor") return "labor";
    if (k === "kit" || k === "assembly") return "kit";
    if (k === "test") return "test";
    if (k === "fee") return /\bcity\b|\bwater\b|\butilit|\bfiling\b|\bjurisdiction/i.test(l.description) ? "fee" : null;
    const pk = (l.pricebook_kind ?? "").toLowerCase();
    if (pk === "labor" || l.labor_techs) return "labor";
    if (pk === "material") return "kit";
    if (pk === "test_fee") return "test";
    return null;
}

const money = (l: EditorLine) => Number(l.line_total) || 0;

/**
 * Device sections in the order the quote first names them, then any device
 * picked for "+ Add repair" with no lines yet is NOT shown (nothing to group),
 * then "Whole job", then "No device yet". Lines keep their stored order inside
 * each section.
 */
export function groupLines(lines: readonly EditorLine[], devices: readonly EditorDevice[]): LineSection[] {
    const byId = new Map(devices.map((d) => [d.id, d]));
    const order: string[] = [];
    const buckets = new Map<string, EditorLine[]>();
    const whole: EditorLine[] = [];
    const none: EditorLine[] = [];
    for (const l of lines) {
        if (l.asset_id) {
            if (!buckets.has(l.asset_id)) {
                buckets.set(l.asset_id, []);
                order.push(l.asset_id);
            }
            buckets.get(l.asset_id)!.push(l);
        } else if (l.whole_job || isAssemblyNote(l)) whole.push(l);
        else none.push(l);
    }
    const section = (key: string, kind: LineSection["kind"], device: EditorDevice | null, ls: EditorLine[]) => {
        const has = { labor: false, kit: false, test: false, fee: false };
        for (const l of ls) {
            const p = pieceOf(l);
            if (p) has[p] = true;
        }
        return { key, kind, device, lines: ls, subtotal: ls.reduce((s, l) => s + money(l), 0), has };
    };
    const out: LineSection[] = order.map((id) => {
        const ls = buckets.get(id)!;
        return section(id, "device", byId.get(id) ?? deviceFromLine(ls[0]), ls);
    });
    if (whole.length > 0) out.push(section("whole", "whole", null, whole));
    out.push(section("none", "none", null, none));
    return out;
}

/** What dropping one line onto another does on the grouped list. */
export type GroupedDrop =
    | { kind: "none" }
    | { kind: "refused"; message: string }
    /** `order` is every line id, top to bottom; `to` is set when the line changes group. */
    | { kind: "move"; order: string[]; to: LineSection | null };

/**
 * Drop line `dragId` onto line `targetId` with "group by device" on (10/28).
 *
 * The move is worked out in the order the office SEES — the groups laid end to
 * end — not the stored order, where one device's lines can sit apart. So the
 * saved order matches the screen, and moving a device's first line never
 * swaps which device comes first. The dragged line takes the target's place,
 * the same as the plain list's grab handle: moving down lands below the
 * target, moving up lands above it. Dropping into another group also means
 * "tag it to that group" (`to`). A line inside a repair option may not leave it.
 */
export function groupedDropOrder(
    lines: readonly EditorLine[],
    devices: readonly EditorDevice[],
    dragId: string,
    targetId: string,
): GroupedDrop {
    if (dragId === targetId) return { kind: "none" };
    const sections = groupLines(lines, devices);
    const from = sections.find((s) => s.lines.some((l) => l.id === dragId));
    const to = sections.find((s) => s.lines.some((l) => l.id === targetId));
    if (!from || !to) return { kind: "none" };
    const shown = sections.flatMap((s) => s.lines);
    const toIndex = shown.findIndex((l) => l.id === targetId);
    const refusal = refuseCrossingOption(shown, dragId, toIndex);
    if (refusal) return { kind: "refused", message: refusal };
    return {
        kind: "move",
        order: moveLine(shown, dragId, toIndex).map((l) => l.id),
        to: from.key === to.key ? null : to,
    };
}

/** Lines the send warning counts: no device, not "Whole job", not inside a repair option. */
export function untaggedLines(lines: readonly EditorLine[]): EditorLine[] {
    // The "make and model may vary" note is words for the whole quote, never a device's line.
    return lines.filter((l) => !l.asset_id && !l.whole_job && !l.option_id && !isAssemblyNote(l));
}

/** Devices the quote's lines already name, in first-mention order. */
export function devicesOnQuote(lines: readonly EditorLine[]): string[] {
    const seen: string[] = [];
    for (const l of lines) if (l.asset_id && !seen.includes(l.asset_id)) seen.push(l.asset_id);
    return seen;
}

/**
 * A shared fee (one line, qty N, for N devices) can split one per device when
 * N matches the devices on the quote and it is not on a repair option.
 */
export function canSplitShared(l: EditorLine, quoteDeviceIds: readonly string[]): boolean {
    const qty = Number(l.qty);
    if (l.asset_id || l.option_id) return false;
    return Number.isInteger(qty) && qty >= 2 && qty === quoteDeviceIds.length;
}

/** Search the device menu: every word must match size, name, type, serial or location. */
export function matchDevices(devices: readonly EditorDevice[], query: string): EditorDevice[] {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return [...devices];
    return devices.filter((d) => {
        const type = deviceTypeChip(d) ?? "";
        const long =
            { RPZ: "reduced pressure rp", DC: "double check", DCVA: "double check", DCDA: "double check detector" }[
                type
            ] ?? "";
        const hay = [
            sizeBadge(d.size_label),
            d.size_label,
            deviceTitle(d),
            d.name,
            type,
            long,
            "sn",
            d.serial_number,
            d.location_description,
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
        return words.every((w) => hay.includes(w));
    });
}

/** "Group by device" is on unless the person turned it off (remembered per browser). */
export const GROUP_PREF_KEY = "gwc.estimate.groupByDevice";
export function readGroupPref(): boolean {
    try {
        return window.localStorage.getItem(GROUP_PREF_KEY) !== "off";
    } catch {
        return true;
    }
}
export function writeGroupPref(on: boolean): void {
    try {
        window.localStorage.setItem(GROUP_PREF_KEY, on ? "on" : "off");
    } catch {
        /* private window: the switch still works for this visit */
    }
}
